import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Button, toaster } from '@saleshandy/design-system';
import { AlertTriangle } from '@saleshandy/icons';
import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import Suspense from '../../shared/components/suspense';
import LeadListing from './components/lead-listing';
import LeadsSidebar from './components/leads-filter';
import TotalCredits from './components/total-credits/total-credits';
import {
  BULK_ACTION_TIMEOUT,
  IProps,
  LeadFinderTabKey,
  LeadRevealType,
  MAX_POLLING_LIMIT,
} from './type';
import RecentSearch from './components/recent-search';
import SearchingEmpty from './components/searching-empty';
import LeadsHeaderSkeleton from './components/leadsSkeleton/leadsHeaderSkeleton';
import { useLeadFinder } from './components/hooks/useLeadFinder';
import { useLeadBulkAction } from './components/hooks/useLeadBulkReveal';
import NoSearchResult from './components/no-search-result';
import { getLeadsActiveTab, getLeadListingPageSize } from './helpers/helper';
import IndividualLeadsSkeleton from './components/leadsSkeleton/individualLeadsSkeleton';
import { RootState } from '../../store/root-reducer';
import NoSearchResultIcon from '../../shared/components/images/no-search-result-icon';
import {
  getUserTrackingId,
  leadFinderViewOnly,
} from '../../shared/utils/user-details';
import { AnalyticsEvents } from '../../shared/enums/analytics';
import WatchVideoModal from '../home/components/watch-video-modal';
import {
  executeOnRequestStatus,
  getIsRequestPending,
  redirectWithoutRefresh,
} from '../../shared/utils';
import { finishOnboardingRequest } from '../sequence/extra-actions';
import {
  OnboardingSteps,
  ProfileProgressSteps,
  UserSettingCode,
} from '../../shared/types/user-setting';
import {
  getUserSettingsRequest,
  setDemoAccountEntryPoint,
  updateApplicationState,
} from '../home/home-slice';
import {
  updateLeadFinderVersionRequest,
  getPostLoadMetaRequest,
  exploreDemoAccountRequest,
} from '../home/extra-actions';
import { handleOnboarding } from '../../shared/utils/handle-onboarding-routing';
import updateProfileProgressRequest from '../../shared/components/getting-started-onboarding/extra-actions';
import { useLeadApiState } from './components/hooks/useLeadApiState';
import { useSavedLeadFilter } from './components/hooks/useLeadSavedFilter';
import { usePageVisibility } from '../../shared/utils/hooks';
import {
  fetchLeadStatusPoll,
  getLeads,
  getSavedLeads,
} from './helpers/leads.api';
import store from '../../store';
import { setShowAiSearchPopup } from './lead-slice';
import AiSearchPopup from './components/ai-search-popup/ai-search-popup';
import { userOsIdentifier } from './components/ai-search-bar/helper';
import { handleDemoAccountModuleViewed } from '../home/utils/helper';
import { StorageUtil } from '../../shared/utils/storage';
import NewLeadFinderBanner from './components/new-lead-finder-banner';
import Alert from '../../shared/design-system/components/alert';
import { computeLeadRevealCredits } from '../v2-lead/helpers/helper';

const Leads: React.FC<IProps> = () => {
  const location: any = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [activeTabKey, setActiveTabKey] = useState(getLeadsActiveTab(location));
  const [selectedLeads, setSelectedLeads] = useState([]);
  const { savedFilters, setSavedFilters } = useSavedLeadFilter(activeTabKey);
  const [emailRevealingLeads, setEmailRevealingLeads] = useState<
    Record<number, boolean>
  >({});
  const [emailPhoneRevealingLeads, setEmailPhoneRevealingLeads] = useState<
    Record<number, boolean>
  >({});
  const {
    isLoading,
    leadsData,
    apiParam,
    savedApiParam,
    handlePagination,
    handleRecentSearch,
    handleGeneratePayload,
    handleAppendLead,
    setLeadsData,
    handleRouteChange,
    searchLeads,
    userEmail,
    populateRevealingLeadsStateAfterSearch,
  } = useLeadFinder(
    activeTabKey,
    setSavedFilters,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
  );

  const { leadTags, isLeadTagsLoading, fetchLeadTags } = useLeadApiState();

  useEffect(() => {
    if (activeTabKey === LeadFinderTabKey.SAVED_TAB) {
      fetchLeadTags(true);
    }
  }, [activeTabKey]);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const loginUserEmail = useSelector((state: RootState) => state?.home?.email);
  const getUserSettingsRequestStatus = useSelector(
    (state: RootState) => state?.home.getUserSettingsRequest.status,
  );

  const meta = useSelector((state: RootState) => state?.home.meta);
  const isDemoAccount = useSelector(
    (state: RootState) => state?.home.isDemoAccount,
  );
  const exploreDemoAccountRequestStatus = useSelector(
    (state: RootState) => state?.home.exploreDemoAccountRequest.status,
  );
  const profileProgress = useSelector(
    (state: RootState) => state?.home.profileProgress,
  );
  const firstName = useSelector((state: RootState) => state?.home.firstName);
  const leadFinder = useSelector((state: RootState) => state?.home.leadFinder);
  const leadFinderPlanCode = useSelector(
    (state: RootState) => state?.home?.leadFinderSubscription?.planCode,
  );
  const updateLeadFinderVersionRequestStatus = useSelector(
    (state: RootState) => state?.home.updateLeadFinderVersionRequest.status,
  );

  const { showAiSearchPopup } = useSelector((state: RootState) => state?.leads);
  const userUrls =
    JSON.parse(StorageUtil.getItem('storedUrls'))?.[loginUserEmail] || [];
  const [storedUrls, setStoredUrls] = useState(userUrls || []);
  const [
    isWatchVideoModalVisible,
    setIsWatchVideoModalVisible,
  ] = useState<boolean>(false);

  const [showPhoneNumberNudge, setShowPhoneNumberNudge] = useState(false);
  const [ignorePhoneNumberNudge, setIgnorePhoneNumberNudge] = useState(false);

  // Determine if banner should be shown (for new users who haven't switched yet)
  const shouldShowNewLeadFinderBanner =
    (!leadFinder?.isVersionLocked &&
      leadFinder?.hasLeadFinderV2Access &&
      leadFinder?.version === null) ||
    !leadFinder?.hasLeadFinderV2Access;

  // Determine if deprecation banner should be shown (for users on v1 with old lead finder plan)
  // Old plan: planCode does not start with 'lf'; new plan: planCode starts with 'lf'
  const isOldLeadFinderPlan =
    leadFinderPlanCode && !leadFinderPlanCode?.startsWith('lf');
  const shouldShowDeprecationBanner =
    !leadFinder?.isVersionLocked &&
    leadFinder?.hasLeadFinderV2Access &&
    leadFinder?.version !== null &&
    isOldLeadFinderPlan;

  const {
    handleCheckUnCheckSingleLead,
    handleSelectAllLead,
    isBulkRevealing,
    setIsBulkRevealing,
    disabledLeads,
    setDisabledLeads,
    isSelectAllIndeterminate,
    selectedAllLeadsCount,
    setSelectedAllLeadsCount,
    isSelectedAllLeads,
    setIsSelectedAllLeads,
    handleClearSelectedAllBulkAction,
    handleSelectAllBulkActionLead,
    deSelectedLeads,
    leadCredits,
    topPeopleLimit,
    leadCreditsState,
    setLeadsCredits,
    handleResetStateAfterAnyAction,
  } = useLeadBulkAction(
    leadsData,
    selectedLeads,
    setSelectedLeads,
    activeTabKey,
  );
  const keywordInputRef: any = useRef();

  const renderComponents = () =>
    Array.from({ length: 4 }, (_, index) => (
      <IndividualLeadsSkeleton key={index} />
    ));

  const onCompleteOnboardingProcess = () => {
    if (!isEmpty(meta)) {
      const onboardingStep = meta.find(
        (setting) => setting.code === UserSettingCode.Onboarding,
      );

      if (onboardingStep?.value === OnboardingSteps.Step2) {
        dispatch(finishOnboardingRequest());
        dispatch(updateApplicationState(true));
        setIsWatchVideoModalVisible(true);

        const currentPath = location.pathname + location.search;
        handleOnboarding(meta, currentPath, '', true);
        setTimeout(() => {
          history.push({
            search: '',
          });
        }, 2000);
      }
    }
  };

  const onVideoModalClose = () => {
    setIsWatchVideoModalVisible(false);
    dispatch(getUserSettingsRequest());
  };

  const updateWatchVideoProfileProgressStep = () => {
    if (profileProgress) {
      const watchVideoStep = profileProgress.find(
        (step) =>
          step.profileProgressStep.stepName === ProfileProgressSteps.WatchVideo,
      );
      if (!watchVideoStep.isCompleted) {
        updateProfileProgressRequest({
          step: ProfileProgressSteps.WatchVideo,
          isCompleted: true,
        });
      }
    }
  };

  // Auto-redirect new plan users (planCode starts with 'lf') to the new Lead Finder,
  // unless they have explicitly chosen the old version (version === 'v1')
  useEffect(() => {
    if (
      leadFinderPlanCode?.startsWith('lf') &&
      leadFinder?.hasLeadFinderV2Access &&
      leadFinder?.version !== 'v1'
    ) {
      redirectWithoutRefresh('/v2/leads');
    }
  }, [
    leadFinderPlanCode,
    leadFinder?.hasLeadFinderV2Access,
    leadFinder?.version,
  ]);

  useEffect(() => {
    // Lead Finder Page Loaded Event
    window.analytics?.track?.({
      userId: getUserTrackingId(),
      event: AnalyticsEvents.LoadedAPage,
      properties: {
        url: document.location.href,
      },
    });

    handleDemoAccountModuleViewed('lead_finder');
  }, []);

  const isPageVisible = usePageVisibility();
  const timerIdRef = useRef(null);
  const pollingAttemptsRef = useRef(0);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [isFirstPollRequest, setIsFirstPollRequest] = useState(true);
  const [
    leadFinderRevealType,
    setLeadFinderRevealType,
  ] = useState<LeadRevealType>(LeadRevealType.EMAIL);

  const {
    home: { shAccountId },
  } = store.getState();

  const handleFetchLeadsAfterBulkActions = async (
    enablePolling = false,
    newPolling = false,
    leadIds = [],
  ) => {
    try {
      if (newPolling) {
        const leadIdsToSend = leadIds?.length
          ? leadIds
          : leadsData?.profiles?.map((ele) => ele.id) ?? [];
        if (!leadIdsToSend?.length) {
          setIsPollingEnabled(false);
          setIsBulkRevealing(false);
          setEmailRevealingLeads({});
          setEmailPhoneRevealingLeads({});
          handleResetStateAfterAnyAction();
          dispatch(getPostLoadMetaRequest());
          return;
        }
        const res = await fetchLeadStatusPoll({
          ids: leadIdsToSend,
          accountId: shAccountId,
          revealType: leadFinderRevealType,
        });
        if (res?.payload?.length) {
          const updatedEmailRevealingLeads = res.payload
            ?.filter((lead) => emailRevealingLeads[lead.id])
            ?.map((lead) => [lead.id, false]);
          setEmailRevealingLeads((prev) => ({
            ...prev,
            ...Object.fromEntries(updatedEmailRevealingLeads),
          }));
          const updatedEmailPhoneRevealingLeads = res.payload
            ?.filter((lead) => emailPhoneRevealingLeads[lead.id])
            ?.map((lead) => [lead.id, false]);
          setEmailPhoneRevealingLeads((prev) => ({
            ...prev,
            ...Object.fromEntries(updatedEmailPhoneRevealingLeads),
          }));
          setLeadsData((prevState) => ({
            ...prevState,
            profiles: (prevState.profiles || []).map((profile) => {
              const updatedLead = res.payload.find(
                (lead) => lead.id === profile.id,
              );
              const reReveal = updatedLead?.reReveal;
              const { tags, teaser } = profile;
              return updatedLead
                ? { ...updatedLead, tags, ...(reReveal ? { teaser } : {}) }
                : profile;
            }),
          }));
        }
        setIsBulkRevealing(false);
        dispatch(getPostLoadMetaRequest());
        if (enablePolling) {
          setIsPollingEnabled(true);
          // handleResetStateAfterAnyAction();
        }
        return;
      }
      let res: any = {};
      if (activeTabKey === LeadFinderTabKey.PEOPLE_TAB) {
        res = await getLeads(apiParam);
      } else {
        res = await getSavedLeads(savedApiParam);
      }
      if (res?.payload) {
        setLeadsData(res.payload);
        setIsBulkRevealing(false);
        populateRevealingLeadsStateAfterSearch(res?.payload?.profiles);
        dispatch(getPostLoadMetaRequest());
        if (enablePolling) {
          setIsPollingEnabled(true);
          // handleResetStateAfterAnyAction();
        }
      }
    } catch (e: any) {
      toaster.error(e?.message);
    }
  };

  useEffect(() => {
    const startPolling = () => {
      timerIdRef.current = setInterval(() => {
        pollingAttemptsRef.current += 1;
        const shouldContinuePolling =
          leadsData?.profiles?.some((ele) => ele.isRevealing) ||
          Object.entries(emailRevealingLeads).some((ele) => ele[1]) ||
          Object.entries(emailPhoneRevealingLeads).some((ele) => ele[1]);
        if (
          !shouldContinuePolling ||
          pollingAttemptsRef.current >= MAX_POLLING_LIMIT
        ) {
          setIsPollingEnabled(false);
          setEmailRevealingLeads({});
          setEmailPhoneRevealingLeads({});
          handleFetchLeadsAfterBulkActions(); // calling last search api to get few extra fields and update it.
        } else {
          setIsPollingEnabled(true);
          handleFetchLeadsAfterBulkActions(false, true);
          if (isFirstPollRequest) {
            // handleResetStateAfterAnyAction();
            setIsFirstPollRequest(false);
          }
        }
      }, BULK_ACTION_TIMEOUT);
    };

    const stopPolling = () => {
      setIsFirstPollRequest(true);
      clearInterval(timerIdRef.current);
    };

    if (isPageVisible && isPollingEnabled) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [
    isPageVisible,
    isPollingEnabled,
    leadsData,
    emailRevealingLeads,
    emailPhoneRevealingLeads,
  ]);

  useEffect(() => {
    setStoredUrls(userUrls);
  }, [location]);

  useEffect(() => {
    const { hash, search } = location;
    const searchParams = new URLSearchParams(search);
    const isRestrictedV1Access = leadFinder?.restrictedV1Access === true;

    const ensureSavedTabParams = () => {
      if (!searchParams.has('start')) {
        searchParams.set('start', '1');
      }
      if (!searchParams.has('take')) {
        searchParams.set('take', getLeadListingPageSize().toString());
      }
    };

    if (hash.includes(LeadFinderTabKey.SAVED_TAB)) {
      setActiveTabKey(LeadFinderTabKey.SAVED_TAB);
      // Ensure pagination params are in URL when landing directly on saved tab
      if (!searchParams.has('start') || !searchParams.has('take')) {
        ensureSavedTabParams();
        history.replace({
          search: searchParams.toString(),
          hash: LeadFinderTabKey.SAVED_TAB,
        });
      }
    } else if (isRestrictedV1Access) {
      // When people tab is restricted, force redirect to saved tab with params
      setActiveTabKey(LeadFinderTabKey.SAVED_TAB);
      ensureSavedTabParams();
      history.replace({
        search: searchParams.toString(),
        hash: LeadFinderTabKey.SAVED_TAB,
      });
    } else {
      setActiveTabKey(LeadFinderTabKey.PEOPLE_TAB);
    }

    // Check if no #<tabKey> is present in the current route
    if (
      !hash ||
      !(
        hash.includes(LeadFinderTabKey.SAVED_TAB) ||
        hash.includes(LeadFinderTabKey.PEOPLE_TAB)
      )
    ) {
      // Redirect to the same path with hash added (or #saved with params if restricted)
      if (isRestrictedV1Access) {
        ensureSavedTabParams();
      }
      history.replace({
        search: searchParams.toString(),
        hash: isRestrictedV1Access
          ? LeadFinderTabKey.SAVED_TAB
          : LeadFinderTabKey.PEOPLE_TAB,
      });
    }
  }, [location, history, leadFinder?.restrictedV1Access]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getUserSettingsRequestStatus,
      onSuccess: onCompleteOnboardingProcess,
    });
  }, [getUserSettingsRequestStatus]);

  useEffect(() => {
    const quotaRemaining =
      leadCreditsState?.home?.getPostLoadMetaResponse
        ?.accountUsageQuotaRemaining;
    const leadReveal = quotaRemaining?.['LEAD.REVEAL'];
    const bucket1 = quotaRemaining?.['LEAD.REVEAL.BUCKET1'];
    const bucket2 = quotaRemaining?.['LEAD.REVEAL.BUCKET2'];
    const isBucket1Frozen = leadCreditsState?.home?.leadFinder?.isBucket1Frozen;
    const { availableCredits } = computeLeadRevealCredits(
      leadReveal,
      bucket1,
      bucket2,
      {
        isBucket1Frozen,
      },
    );
    setLeadsCredits(availableCredits);
  }, [leadCreditsState, leadsData]);

  useEffect(() => {
    if (activeTabKey === LeadFinderTabKey.SAVED_TAB && isSelectedAllLeads) {
      const filteredLeads = leadsData?.profiles.filter((l) =>
        disabledLeads.filter((dl) => dl.id !== l.id),
      );
      setSelectedLeads(filteredLeads);
    }
  }, [leadsData, isSelectedAllLeads, disabledLeads]);

  useEffect(() => {
    if (activeTabKey !== LeadFinderTabKey.PEOPLE_TAB) return;

    const handleKeyDown = (e) => {
      const platform = userOsIdentifier();
      const isMac = platform.includes('mac');

      if (
        (isMac && e.metaKey && e.key.toLowerCase() === 'k') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')
      ) {
        e.preventDefault();
        dispatch(setShowAiSearchPopup(true));
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeTabKey]);

  // Handle switch to new lead finder
  const handleSwitchToNewLeadFinder = () => {
    dispatch(updateLeadFinderVersionRequest('v2'));
  };

  return (
    <>
      <Container
        fluid
        className={`leads-container ${
          shouldShowNewLeadFinderBanner || shouldShowDeprecationBanner
            ? 'leads-container--with-banner'
            : ''
        } ${
          shouldShowDeprecationBanner
            ? 'leads-container--with-deprecation-banner'
            : ''
        }`}
      >
        <ErrorBoundaryWrapper>
          <Suspense>
            {shouldShowDeprecationBanner && (
              <div className="lead-finder-custom-banner">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="lead-finder-custom-banner-text">
                    Lead Finder just got better. Our new plans are now live -
                    get more leads, richer data, and faster results than ever
                    before.
                    <span>The old version will be deprecated soon.</span>
                  </div>
                  <div className="divider" />
                  <div>
                    <Button
                      className="lead-finder-custom-banner-link"
                      onClick={(e) => {
                        e.preventDefault();
                        redirectWithoutRefresh(
                          '/billing/upgrade-plan/lead-finder',
                        );
                      }}
                    >
                      Explore Now
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {shouldShowNewLeadFinderBanner && (
              <NewLeadFinderBanner
                onSwitchClick={handleSwitchToNewLeadFinder}
                isLoading={getIsRequestPending(
                  updateLeadFinderVersionRequestStatus,
                )}
              />
            )}
            <Row>
              <Col>
                <Row className="leads-content">
                  <LeadsSidebar
                    {...{
                      isSidebarExpanded,
                      setIsSidebarExpanded,
                      handleRecentSearch,
                      userEmail,
                      handleGeneratePayload,
                      setActiveTabKey,
                      activeTabKey,
                      handlePagination,
                      setLeadsData,
                      setSelectedLeads,
                      handleRouteChange,
                      leadTags,
                      isLeadTagsLoading,
                      fetchLeadTags,
                      handleResetStateAfterAnyAction,
                      savedFilters,
                      setSavedFilters,
                      showPhoneNumberNudge,
                      setShowPhoneNumberNudge,
                      setIgnorePhoneNumberNudge,
                      searchLeads,
                      isLoading,
                      keywordInputRef,
                    }}
                  />
                  <div
                    className={`${
                      !isSidebarExpanded
                        ? 'collpase-leads-component'
                        : 'leads-components'
                    } collpase-leads-component`}
                  >
                    {!isLoading && (
                      <TotalCredits
                        {...{
                          topPeopleLimit,
                          leadCredits,
                          handleSelectAllLead,
                          handleCheckUnCheckSingleLead,
                          selectedLeads,
                          leadsData,
                          setIsBulkRevealing,
                          isBulkRevealing,
                          apiParam,
                          activeTabKey,
                          setSelectedLeads,
                          isSelectAllIndeterminate,
                          disabledLeads,
                          selectedAllLeadsCount,
                          setSelectedAllLeadsCount,
                          isSelectedAllLeads,
                          setIsSelectedAllLeads,
                          handleClearSelectedAllBulkAction,
                          handleSelectAllBulkActionLead,
                          deSelectedLeads,
                          leadTags,
                          isLeadTagsLoading,
                          fetchLeadTags,
                          handleResetStateAfterAnyAction,
                          savedFilters,
                          setSavedFilters,
                          handleFetchLeadsAfterBulkActions,
                          setShowPhoneNumberNudge,
                          showPhoneNumberNudge,
                          ignorePhoneNumberNudge,
                          setIgnorePhoneNumberNudge,
                          leadFinderRevealType,
                          setLeadFinderRevealType,
                          emailRevealingLeads,
                          setEmailRevealingLeads,
                          emailPhoneRevealingLeads,
                          setEmailPhoneRevealingLeads,
                          isDemoAccount,
                        }}
                      />
                    )}
                    {userUrls?.length === 0 &&
                      !isLoading &&
                      leadsData?.pagination === null && <SearchingEmpty />}

                    {!isLoading &&
                      userUrls &&
                      userUrls?.length > 0 &&
                      (!location?.search || leadsData?.pagination === null) &&
                      activeTabKey === LeadFinderTabKey?.PEOPLE_TAB && (
                        <RecentSearch
                          {...{
                            storedUrls,
                            setStoredUrls,
                            loginUserEmail,
                            searchLeads,
                          }}
                        />
                      )}
                    {!isLoading &&
                      leadsData &&
                      location?.search &&
                      leadsData?.profiles?.length === 0 &&
                      leadsData?.pagination?.total === 0 && (
                        <div className="no-search-lead">
                          <NoSearchResult
                            description={
                              <div>
                                Results matching this query could not be
                                displayed. Please try refining your search{' '}
                                <br /> or clearing some of your filters.
                              </div>
                            }
                            title="No results found"
                          >
                            <NoSearchResultIcon />
                          </NoSearchResult>
                        </div>
                      )}
                    {!isLoading &&
                      leadsData &&
                      location?.search &&
                      // eslint-disable-next-line no-prototype-builtins
                      (!leadsData?.hasOwnProperty('profiles') ||
                        // eslint-disable-next-line no-prototype-builtins
                        !leadsData?.hasOwnProperty('pagination')) && (
                        <div className="no-search-lead">
                          <NoSearchResult
                            description={
                              <div>
                                Something went wrong <br /> Please try again
                                after sometime
                              </div>
                            }
                            title="Error Occurred"
                          >
                            <NoSearchResultIcon />
                          </NoSearchResult>
                        </div>
                      )}
                    {isLoading && location?.search && !leadFinderViewOnly() && (
                      <LeadsHeaderSkeleton activeTabKey={activeTabKey} />
                    )}
                    {isLoading && location?.search && renderComponents()}

                    {leadsData?.profiles &&
                      leadsData?.profiles?.length > 0 &&
                      location?.search && (
                        <>
                          <LeadListing
                            {...{
                              topPeopleLimit,
                              leadCredits,
                              isLoading,
                              leadsData,
                              handlePagination,
                              handleCheckUnCheckSingleLead,
                              selectedLeads,
                              handleAppendLead,
                              isBulkRevealing,
                              apiParam,
                              savedApiParam,
                              setLeadsData,
                              activeTabKey,
                              setSelectedLeads,
                              disabledLeads,
                              setDisabledLeads,
                              handleRouteChange,
                              setLeadsDataState: setLeadsData,
                              deSelectedLeads,
                              isSelectedAllLeads,
                              handleResetStateAfterAnyAction,
                              handleFetchLeadsAfterBulkActions,
                              isFirstPollRequest,
                              leadFinderRevealType,
                              setLeadFinderRevealType,
                              emailRevealingLeads,
                              setEmailRevealingLeads,
                              emailPhoneRevealingLeads,
                              setEmailPhoneRevealingLeads,
                            }}
                          />
                        </>
                      )}
                  </div>
                </Row>
              </Col>
            </Row>
          </Suspense>
        </ErrorBoundaryWrapper>
      </Container>

      {showAiSearchPopup && (
        <AiSearchPopup
          show={showAiSearchPopup}
          onClose={() => dispatch(setShowAiSearchPopup(false))}
        />
      )}

      {isWatchVideoModalVisible && (
        <WatchVideoModal
          show={isWatchVideoModalVisible}
          isGettingStartedVideModalShowThroughIcon={false}
          firstName={firstName}
          onClose={() => {
            onVideoModalClose();
          }}
          updateWatchVideoProfileProgressStep={
            updateWatchVideoProfileProgressStep
          }
          isLeadFinderOnboarding={true}
          sendExploreDemoAccountRequest={() => {
            dispatch(setDemoAccountEntryPoint('onboarding_screen'));
            dispatch(exploreDemoAccountRequest());
          }}
          exploreDemoAccountRequestStatusPending={getIsRequestPending(
            exploreDemoAccountRequestStatus,
          )}
        />
      )}
    </>
  );
};

export default Leads;
